import React, { useState } from 'react'
import styled from '@emotion/styled'
import { ResolvedDay, OverrideType } from '../types'
import {
  DAY_STATUS_COLORS,
  DAY_STATUS_BORDER_COLORS,
  TIME_SOURCE_COLORS,
  WEEKEND_BG,
  CANCEL_REASON_LABELS,
  ADD_REASON_LABELS,
  OVERRIDE_TYPE_LABELS,
  CUTOFF_EMOJI,
  DROPOFF_EMOJI,
} from '../constants'
import { formatCutoffLabel, formatDropoffLabel } from '../utils'
import { CancelReason, AddReason, TimeSource } from '../types'

interface Props {
  day: ResolvedDay
  dayOfMonth: number
  isWeekend: boolean
  isToday: boolean
  holidayName?: string
  onClick: (date: string) => void
}

function buildTooltip(day: ResolvedDay): string {
  const lines: string[] = [day.date]

  if (day.status === 'service') {
    lines.push('Status: Service day')
  } else if (day.status === 'ad-hoc-on') {
    lines.push('Status: Ad hoc day on')
  } else if (day.status === 'cancelled') {
    lines.push('Status: Cancelled')
  } else {
    lines.push('Status: No service')
  }

  if (day.override) {
    lines.push(`Override: ${OVERRIDE_TYPE_LABELS[day.override.overrideType]}`)
    if (day.override.reason) {
      const label =
        day.override.overrideType === OverrideType.Add
          ? ADD_REASON_LABELS[day.override.reason as AddReason]
          : CANCEL_REASON_LABELS[day.override.reason as CancelReason]
      if (label) {
        lines.push(`Reason: ${label}`)
      }
    }
    if (day.override.notes) {
      lines.push(`Notes: ${day.override.notes}`)
    }
  }

  if (day.cutoff) {
    lines.push(`Cutoff: ${formatCutoffLabel(day.cutoff)} (${day.cutoffSource})`)
  }

  if (day.dropoffTime != null) {
    lines.push(
      `Dropoff: ${formatDropoffLabel(day.dropoffTime)} (${day.dropoffSource})`,
    )
  }

  return lines.join('\n')
}

const CalendarDay: React.FC<Props> = ({
  day,
  dayOfMonth,
  isWeekend,
  isToday,
  holidayName,
  onClick,
}) => {
  const [showHolidayTooltip, setShowHolidayTooltip] = useState(false)
  const hasService = day.status === 'service' || day.status === 'ad-hoc-on'
  const bgColor =
    day.status === 'no-schedule' && isWeekend
      ? WEEKEND_BG
      : DAY_STATUS_COLORS[day.status]
  const borderColor = DAY_STATUS_BORDER_COLORS[day.status]

  return (
    <Cell
      data-testid={`calendar-day-${day.date}`}
      data-status={day.status}
      bgColor={bgColor}
      borderColor={day.status !== 'no-schedule' ? borderColor : 'transparent'}
      isToday={isToday}
      title={buildTooltip(day)}
      onClick={() => onClick(day.date)}
    >
      <DayNumber isToday={isToday}>{dayOfMonth}</DayNumber>
      {hasService && day.cutoff && (
        <TimeLabel
          data-testid={`cutoff-label-${day.date}`}
          color={TIME_SOURCE_COLORS[day.cutoffSource as TimeSource]}
        >
          {CUTOFF_EMOJI}
          {formatCutoffLabel(day.cutoff)}
        </TimeLabel>
      )}
      {hasService && day.dropoffTime != null && (
        <TimeLabel
          data-testid={`dropoff-label-${day.date}`}
          color={TIME_SOURCE_COLORS[day.dropoffSource as TimeSource]}
        >
          {DROPOFF_EMOJI}
          {formatDropoffLabel(day.dropoffTime)}
        </TimeLabel>
      )}
      {holidayName && (
        <HolidayIndicator
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={() => setShowHolidayTooltip(true)}
          onMouseLeave={() => setShowHolidayTooltip(false)}
        >
          <HolidayDot />
          {showHolidayTooltip && <HolidayTooltip>{holidayName}</HolidayTooltip>}
        </HolidayIndicator>
      )}
    </Cell>
  )
}

export default CalendarDay

// ── Styled Components ──

const Cell = styled.div<{
  bgColor: string
  borderColor: string
  isToday: boolean
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 68px;
  padding: 4px 2px;
  background: ${({ bgColor }) => bgColor};
  border: 1.5px solid ${({ borderColor }) => borderColor};
  border-radius: 3px;
  cursor: pointer;
  transition: opacity 0.1s;
  box-shadow: ${({ isToday }) =>
    isToday ? 'inset 0 0 0 2px #1976D2' : 'none'};

  &:hover {
    opacity: 0.8;
  }
`

const DayNumber = styled.span<{ isToday: boolean }>`
  font-size: 12px;
  font-weight: ${({ isToday }) => (isToday ? 700 : 400)};
  color: ${({ isToday }) => (isToday ? '#1976D2' : '#424242')};
  line-height: 1.2;
`

const TimeLabel = styled.span<{ color: string }>`
  font-size: 9px;
  font-weight: 600;
  color: ${({ color }) => color};
  line-height: 1.2;
  margin-top: 2px;
  white-space: nowrap;
`

const HolidayTooltip = styled.span`
  position: absolute;
  bottom: calc(100% + 5px);
  left: 50%;
  transform: translateX(-50%);
  background: #424242;
  color: #fff;
  font-size: 10px;
  font-weight: 500;
  padding: 3px 7px;
  border-radius: 3px;
  white-space: nowrap;
  z-index: 200;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #424242;
  }
`

const HolidayDot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ff8f00;
`

const HolidayIndicator = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: auto;
  padding-top: 2px;
  cursor: default;
`
